import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { XMLParser, XmlBuilderOptions } from 'fast-xml-parser';

import { ConfigService } from './config.service';

@Injectable({
  providedIn: 'root',
})
export class XmlHttpService {
  private readonly http = inject(HttpClient);
  private readonly configService = inject(ConfigService);

  public get(url: string): Observable<any> {
    return this.http
      .get(
        `${this.configService.config.apiUrl}${url}${url.includes('?') ? '&' : '?'}key=${
          this.configService.config.apiKey
        }`,
        { responseType: 'text' },
      )
      .pipe(switchMap((xmlString: string) => this.handleXmlResult(xmlString)));
  }

  public post(url: string, body: any): Observable<any> {
    return this.http
      .post(
        `${this.configService.config.apiUrl}${url}`,
        { key: this.configService.config.apiKey, ...body },
        { responseType: 'json' },
      )
      .pipe(switchMap((json: any) => this.handleJsonResult(json)));
  }

  public put(url: string, body: any): Observable<any> {
    return this.http
      .put(
        `${this.configService.config.apiUrl}${url}`,
        { key: this.configService.config.apiKey, ...body },
        { responseType: 'text' },
      )
      .pipe(switchMap((json: any) => this.handleJsonResult(json)));
  }

  public delete(url: string): Observable<any> {
    return this.http
      .delete(
        `${this.configService.config.apiUrl}${url}${url.includes('?') ? '&' : '?'}key=${
          this.configService.config.apiKey
        }`,
        { responseType: 'text' },
      )
      .pipe(switchMap((xmlString: string) => this.handleXmlResult(xmlString)));
  }

  private handleXmlResult(xmlString: string) {
    return new Promise((resolve, reject) => {
      try {
        const options: XmlBuilderOptions = {
          ignoreAttributes: false,
        };
        const parser = new XMLParser(options);
        const result = parser.parse(xmlString);

        let hasError = false;
        let errorMessage: string;
        for (const prop in result) {
          if (result[prop]?.['Success']?.[0] === 'false') {
            hasError = true;
            errorMessage = result[prop]?.['ErrorMessage']?.[0];
          }
        }
        if (hasError) {
          reject(errorMessage);
        } else {
          resolve(result);
        }
      } catch (error) {
        reject(error);
      }
    });
  }

  private handleJsonResult(json: any) {
    return new Promise((resolve, reject) => {
      const result = json.d;
      if (result.Success === false) {
        reject(result.ErrorMessage);
      } else {
        resolve(result);
      }
    });
  }
}
